import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  BatchUploadResult,
  ClassifyResult,
  Dispatch,
  DispatchInput,
  DispatchLine,
  DispatchLineInput,
  DispatchPatch,
  PoShippingDocumentsResponse,
  ShippingDocType,
  UploadShippingDocVars,
} from './shipping-docs-types'

const BASE = '/api/po-shipping-documents'
const KEY = ['po-shipping-documents'] as const

export function usePoShippingDocuments(odooPoId: number | null) {
  return useQuery({
    queryKey: [...KEY, 'po', odooPoId],
    queryFn: () => jsonFetch<PoShippingDocumentsResponse>(`${BASE}/po/${odooPoId}`),
    enabled: odooPoId != null,
    staleTime: 15_000,
  })
}

export function useShippingDocTypes(includeInactive = false) {
  return useQuery({
    queryKey: [...KEY, 'types', includeInactive],
    queryFn: () => jsonFetch<ShippingDocType[]>(`${BASE}/types?include_inactive=${includeInactive}`),
    staleTime: 60_000,
  })
}

function useInvalidating<TVars, TData>(fn: (vars: TVars) => Promise<TData>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) })
}

export function useCreateDispatch() {
  return useInvalidating((vars: { odooPoId: number; body: DispatchInput }) =>
    jsonFetch<{ dispatch: Dispatch }>(`${BASE}/po/${vars.odooPoId}/dispatches`, { method: 'POST', body: vars.body }),
  )
}

export function useUpdateDispatch() {
  return useInvalidating((vars: { dispatchId: number; body: DispatchPatch }) =>
    jsonFetch<{ dispatch: Dispatch }>(`${BASE}/dispatches/${vars.dispatchId}`, { method: 'PATCH', body: vars.body }),
  )
}

export function useSetDispatchLines() {
  return useInvalidating((vars: { dispatchId: number; lines: DispatchLineInput[] }) =>
    jsonFetch<{ lines: DispatchLine[] }>(`${BASE}/dispatches/${vars.dispatchId}/lines`, {
      method: 'PUT',
      body: { lines: vars.lines },
    }),
  )
}

export function useCancelDispatch() {
  return useInvalidating((vars: { dispatchId: number; force: boolean }) =>
    jsonFetch<{ id: number; status: string }>(
      `${BASE}/dispatches/${vars.dispatchId}/cancel?force=${vars.force}`,
      { method: 'POST' },
    ),
  )
}

export function useUploadShippingDocument() {
  return useInvalidating((vars: UploadShippingDocVars) => {
    const form = new FormData()
    form.set('file', vars.file)
    form.set('doc_type_key', vars.docTypeKey)
    if (vars.notes) form.set('notes', vars.notes)
    return jsonFetch<{ unchanged: boolean }>(`${BASE}/dispatches/${vars.dispatchId}/documents`, {
      method: 'POST',
      rawBody: form,
    })
  })
}

export function useArchiveShippingDocument() {
  return useInvalidating((id: number) =>
    jsonFetch<{ id: number; status: string }>(`${BASE}/documents/${id}/archive`, { method: 'POST' }),
  )
}

export function useCreateShippingDocType() {
  return useInvalidating((vars: { key: string; label: string }) =>
    jsonFetch<ShippingDocType>(`${BASE}/types`, { method: 'POST', body: vars }),
  )
}

export function useUpdateShippingDocType() {
  return useInvalidating((vars: { key: string; body: Partial<Pick<ShippingDocType, 'label' | 'active'>> }) =>
    jsonFetch<ShippingDocType>(`${BASE}/types/${vars.key}`, { method: 'PATCH', body: vars.body }),
  )
}

/** Suggest a type for each file from its name and first page. Stores nothing. */
export function useClassifyShippingDocuments() {
  return useMutation({
    mutationFn: (files: File[]) => {
      const form = new FormData()
      files.forEach((f) => form.append('files', f))
      return jsonFetch<ClassifyResult[]>(`${BASE}/classify`, { method: 'POST', rawBody: form })
    },
  })
}

/** File many documents under one dispatch in one request; each file succeeds or fails on its own. */
export function useUploadShippingDocumentsBatch() {
  return useInvalidating((vars: { dispatchId: number; files: File[]; typeKeys: string[]; notes?: string }) => {
    const form = new FormData()
    vars.files.forEach((f) => form.append('files', f))
    vars.typeKeys.forEach((k) => form.append('doc_type_keys', k))
    if (vars.notes) form.set('notes', vars.notes)
    return jsonFetch<BatchUploadResult>(`${BASE}/dispatches/${vars.dispatchId}/documents/batch`, {
      method: 'POST',
      rawBody: form,
    })
  })
}

export function useChangeDocumentType() {
  return useInvalidating((vars: { documentId: number; docTypeKey: string }) =>
    jsonFetch<{ id: number; doc_type_key: string }>(`${BASE}/documents/${vars.documentId}`, {
      method: 'PATCH',
      body: { doc_type_key: vars.docTypeKey },
    }),
  )
}
